/**
 * kb-category-tree.tsx — Cây danh mục BÊN TRONG một kho, của MỘT loại nội dung.
 *
 * - Mỗi tab có cây riêng (`kind`): danh mục Hỏi đáp · nhóm Ảnh và video · thư mục Tài liệu — không dùng chung.
 * - "+" tròn cạnh "Tất cả danh mục" thêm danh mục cấp 1; rê chuột một danh mục hiện: thêm danh mục con, sửa tên, xoá.
 * - Tay kéo ⋮⋮ để sắp xếp trong cùng cấp (kéo thả HTML5) → POST /knowledge-categories/reorder.
 * - Bấm danh mục → lọc bảng theo danh mục đó (gồm danh mục con).
 */
import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ChevronDown, ChevronRight, Folder, FolderPlus, GripVertical, LayoutGrid, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import {
  useKnowledgeCategories, useCreateKnowledgeCategory, useUpdateKnowledgeCategory, useDeleteKnowledgeCategory,
  reorderKnowledgeCategories, type KnowledgeCategory, type KbCategoryKind,
} from '@/hooks/use-knowledge'
import { buildCategoryTree, moveId, type CategoryNode, type KnowledgePerms } from './kb-lib'

const MAX_DEPTH = 3

/** Câu chữ theo loại cây: danh mục (Hỏi đáp) · nhóm (Ảnh và video) · thư mục (Tài liệu). */
export const TREE_WORDS: Record<KbCategoryKind, { one: string; all: string; tab: string; example: string }> = {
  qa: {
    get one() { return i18n.t('aiKnow:tree.words.qa.one') }, get all() { return i18n.t('aiKnow:tree.words.qa.all') },
    get tab() { return i18n.t('aiKnow:tree.words.qa.tab') }, get example() { return i18n.t('aiKnow:tree.words.qa.example') },
  },
  material: {
    get one() { return i18n.t('aiKnow:tree.words.material.one') }, get all() { return i18n.t('aiKnow:tree.words.material.all') },
    get tab() { return i18n.t('aiKnow:tree.words.material.tab') }, get example() { return i18n.t('aiKnow:tree.words.material.example') },
  },
  article: {
    get one() { return i18n.t('aiKnow:tree.words.article.one') }, get all() { return i18n.t('aiKnow:tree.words.article.all') },
    get tab() { return i18n.t('aiKnow:tree.words.article.tab') }, get example() { return i18n.t('aiKnow:tree.words.article.example') },
  },
}
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

export function KbCategoryTree({ baseId, kind, selected, onSelect, perms, totalCount, className }: {
  baseId: string
  /** Cây nào của kho. */
  kind: KbCategoryKind
  /** '' = Tất cả danh mục. */
  selected: string
  onSelect: (id: string) => void
  /** Thêm cần knowledge.create; sửa tên / kéo sắp xếp cần knowledge.update; xoá cần knowledge.delete. */
  perms: KnowledgePerms
  /** Số mục của cả kho trong tab hiện tại (hiện cạnh "Tất cả …"). */
  totalCount?: number
  className?: string
}) {
  const { t } = useTranslation('aiKnow')
  const qc = useQueryClient()
  const { canCreate, canUpdate, canDelete } = perms
  const w = TREE_WORDS[kind]
  const { data: cats, isLoading } = useKnowledgeCategories(baseId, { kind })
  const remove = useDeleteKnowledgeCategory()
  const tree = useMemo(() => buildCategoryTree(cats ?? []), [cats])
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [editing, setEditing] = useState<{ mode: 'add'; parentId: string | null } | { mode: 'rename'; cat: KnowledgeCategory } | null>(null)
  const [dragId, setDragId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | null>(null)

  async function handleDelete(c: KnowledgeCategory) {
    const children = (cats ?? []).filter((x) => x.parentId === c.id).length
    const msg = children
      ? t('tree.confirmDeleteWithChildren', { one: w.one, name: c.name, children })
      : t('tree.confirmDelete', { one: w.one, name: c.name })
    if (!window.confirm(msg)) return
    try {
      await remove.mutateAsync(c.id)
      if (selected === c.id) onSelect('')
      toast.success(t('tree.deleted', { one: w.one }))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function drop(target: CategoryNode) {
    const from = dragId
    setDragId(null); setOverId(null)
    if (!from || from === target.id) return
    const src = (cats ?? []).find((c) => c.id === from)
    if (!src || (src.parentId ?? null) !== (target.parentId ?? null)) {
      toast.info(t('tree.dragSameLevel'))
      return
    }
    const siblings = (cats ?? [])
      .filter((c) => (c.parentId ?? null) === (target.parentId ?? null))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'vi'))
      .map((c) => c.id)
    const next = moveId(siblings, from, target.id)
    // Cập nhật ngay trên giao diện, rồi lưu.
    qc.setQueryData<KnowledgeCategory[]>(['knowledge-categories', baseId, kind], (prev) =>
      (prev ?? []).map((c) => (next.includes(c.id) ? { ...c, sortOrder: next.indexOf(c.id) } : c)))
    try {
      await reorderKnowledgeCategories(baseId, next)
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      void qc.invalidateQueries({ queryKey: ['knowledge-categories', baseId] })
    }
  }

  const renderNode = (n: CategoryNode) => {
    const isOpen = !collapsed.has(n.id)
    const count = n.entryCount ?? 0
    return (
      <li key={n.id}>
        <div
          className={cn(
            'group flex items-center gap-1 rounded-md pr-1 text-sm transition-colors',
            selected === n.id ? 'bg-primary/10 font-medium text-primary' : 'hover:bg-background',
            overId === n.id && dragId && dragId !== n.id && 'ring-1 ring-primary/50',
          )}
          style={{ paddingLeft: 4 + n.depth * 14 }}
          draggable={canUpdate}
          onDragStart={(e) => { setDragId(n.id); e.dataTransfer.effectAllowed = 'move' }}
          onDragEnd={() => { setDragId(null); setOverId(null) }}
          onDragOver={(e) => { if (dragId) { e.preventDefault(); setOverId(n.id) } }}
          onDrop={(e) => { e.preventDefault(); void drop(n) }}
        >
          <GripVertical className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground/60', canUpdate ? 'cursor-grab' : 'invisible')} aria-hidden />
          {n.children.length ? (
            <button
              type="button" className="rounded p-0.5 text-muted-foreground hover:bg-muted" aria-label={isOpen ? t('tree.collapse') : t('tree.expand')}
              onClick={() => setCollapsed((s) => { const x = new Set(s); if (x.has(n.id)) x.delete(n.id); else x.add(n.id); return x })}
            >
              {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          ) : <span className="w-[18px]" />}
          <button type="button" className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 text-left" onClick={() => onSelect(n.id)} title={n.name}>
            <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate">{n.name}</span>
            {count > 0 && <span className="ml-auto shrink-0 text-[11px] text-muted-foreground group-hover:hidden">{count}</span>}
          </button>
          {(canCreate || canUpdate || canDelete) && (
            <span className="hidden shrink-0 items-center group-hover:flex">
              {canCreate && n.depth + 1 < MAX_DEPTH && (
                <button type="button" title={t('tree.addChild', { one: w.one })} className="rounded p-1 text-muted-foreground hover:bg-muted" onClick={() => setEditing({ mode: 'add', parentId: n.id })}>
                  <FolderPlus className="h-3.5 w-3.5" />
                </button>
              )}
              {canUpdate && (
                <button type="button" title={t('tree.rename')} className="rounded p-1 text-muted-foreground hover:bg-muted" onClick={() => setEditing({ mode: 'rename', cat: n })}>
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
              {canDelete && (
                <button type="button" title={t('common:actions.delete')} className="rounded p-1 text-destructive hover:bg-muted" onClick={() => void handleDelete(n)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          )}
        </div>
        {n.children.length > 0 && isOpen && <ul>{n.children.map(renderNode)}</ul>}
      </li>
    )
  }

  return (
    <aside className={cn('h-fit rounded-lg bg-muted/50 p-2', className)} aria-label={t('tree.asideLabel', { one: cap(w.one), tab: w.tab })}>
      <div className={cn('flex items-center gap-1 rounded-md px-1', selected === '' ? 'bg-primary/10 text-primary' : 'hover:bg-background')}>
        <button type="button" className="flex min-w-0 flex-1 items-center gap-2 py-2 text-left text-sm font-medium" onClick={() => onSelect('')}>
          <LayoutGrid className="h-4 w-4 shrink-0" />
          <span className="truncate">{w.all}</span>
          {totalCount != null && <span className="ml-auto text-[11px] font-normal text-muted-foreground">{totalCount}</span>}
        </button>
        {canCreate && (
          <button
            type="button" title={t('tree.add', { one: w.one })} aria-label={t('tree.add', { one: w.one })}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary/40 bg-background text-primary hover:bg-primary hover:text-primary-foreground"
            onClick={() => setEditing({ mode: 'add', parentId: null })}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {isLoading ? (
        <p className="px-2 py-2 text-xs text-muted-foreground">{t('tree.loading')}</p>
      ) : tree.length === 0 ? (
        <p className="px-2 py-2 text-[11px] text-muted-foreground">{t('tree.empty', { one: w.one })}{canCreate && t('tree.emptyHint', { one: w.one, tab: w.tab })}</p>
      ) : (
        <ul className="mt-1 space-y-0.5">{tree.map(renderNode)}</ul>
      )}
      {editing && (
        <CategoryNameDialog
          baseId={baseId}
          kind={kind}
          editing={editing}
          parentName={editing.mode === 'add' && editing.parentId ? (cats ?? []).find((c) => c.id === editing.parentId)?.name : undefined}
          onClose={() => setEditing(null)}
          onCreated={(id) => onSelect(id)}
        />
      )}
    </aside>
  )
}

function CategoryNameDialog({ baseId, kind, editing, parentName, onClose, onCreated }: {
  baseId: string
  kind: KbCategoryKind
  editing: { mode: 'add'; parentId: string | null } | { mode: 'rename'; cat: KnowledgeCategory }
  parentName?: string
  onClose: () => void
  onCreated: (id: string) => void
}) {
  const { t } = useTranslation('aiKnow')
  const create = useCreateKnowledgeCategory()
  const update = useUpdateKnowledgeCategory()
  const [name, setName] = useState(editing.mode === 'rename' ? editing.cat.name : '')
  const busy = create.isPending || update.isPending
  const w = TREE_WORDS[kind]

  async function save() {
    const n = name.trim()
    if (!n) { toast.error(t('tree.enterName', { one: w.one })); return }
    try {
      if (editing.mode === 'rename') {
        await update.mutateAsync({ id: editing.cat.id, data: { name: n } })
        toast.success(t('tree.renamed', { one: w.one }))
      } else {
        const c = await create.mutateAsync({ name: n, knowledgeBaseId: baseId, parentId: editing.parentId, kind })
        toast.success(t('tree.added', { one: w.one }))
        onCreated(c.id)
      }
      onClose()
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{editing.mode === 'rename' ? t('tree.renameTitle', { one: w.one }) : parentName ? t('tree.addChild', { one: w.one }) : t('tree.add', { one: w.one })}</DialogTitle>
          <DialogDescription>
            {parentName ? t('tree.insideParent', { name: parentName }) : ''}{t('tree.onlyFor', { one: cap(w.one), tab: w.tab })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Label htmlFor="kb-cat-name">{t('tree.nameLabel', { one: w.one })}</Label>
          <Input
            id="kb-cat-name" autoFocus value={name} maxLength={120} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void save() } }}
            placeholder={w.example}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Button>
          <Button onClick={() => void save()} disabled={busy}>{editing.mode === 'rename' ? t('common:actions.save') : t('common:actions.add')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
